import groq from "groq";
import { Page } from "../wrappers/Page";
import { usePreviewSubscription } from "../helpers/sanity/sanity";
import { getClient } from "../helpers/sanity/server";
import type { GetStaticProps } from "next";
import { NavType } from "../types";
import { navQuery } from "../sections/Nav";
import React from "react";
import { FooterProps, footerQuery } from "../sections/Footer";
import { CookieSettings } from "../components/CookieSettings";
import { MaxWidth } from "../layouts/MaxWidth";
import { PageTitle } from "../sections/PageTitle";
import { Spacing } from "../layouts/Spacing";

/**
 * Sanity Query
 */

const pageQuery = groq`
  {
    "page": {
      '_id': 'cookies',
      'slug': 'cookies',
      'title': 'Cookies',
    },
    ${footerQuery},
    ${navQuery},
  }
`;

interface PageQueryType {
  page: {
    _id?: string;
    title?: string;
  };
  nav: NavType;
  footer: FooterProps | {};
}

/**
 * Page
 */

interface Props {
  data: {
    post: PageQueryType;
  };
  preview: boolean;
}

const SingletonPage = ({ data, preview }: Props) => {
  const { data: post } = usePreviewSubscription(pageQuery, {
    params: { slug: data.post?.page?._id },
    initialData: data.post,
    enabled: preview && Boolean(data.post?.page?._id),
  });

  if (!post?.page) {
    return <Page notFound={true} nav={post.nav} footer={post.footer} />;
  }

  const { title, _id } = post.page;

  return (
    <Page title={title} notFound={!_id} nav={post.nav} footer={post.footer}>
      <PageTitle title="Cookie instellingen" />
      <MaxWidth size="sm">
        <Spacing size="xs">
          <p>
            MaakJeStap maakt gebruik van cookies voor zover deze noodzakelijk
            zijn voor een goede werking van de website en om de kwaliteit of
            effectiviteit van de website te verbeteren.
          </p>
          <p>Pas hier uw instellingen aan:</p>
        </Spacing>
        <Spacing size="xs">
          <CookieSettings />
        </Spacing>
      </MaxWidth>
    </Page>
  );
};

export default SingletonPage;

/**
 * Static props
 */

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const post: PageQueryType = await getClient(preview).fetch(pageQuery);

  if (!post) return { props: { notFound: true } };

  return {
    props: {
      preview,
      data: { post },
    },
    revalidate: 60,
  };
};
